import { ROLE_LABELS } from "../../../utils/roles";
import React, { useState } from "react";
import {
    Box,
    Typography,
    Paper,
    Grid,
    Chip,
    Avatar,
    Collapse,
    IconButton,
    Divider,
    Stack,
    Tabs,
    Tab,
    Tooltip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PersonIcon from "@mui/icons-material/Person";
import GroupsIcon from "@mui/icons-material/Groups";
import PhoneIcon from "@mui/icons-material/Phone";
import LeaderboardIcon from "@mui/icons-material/Leaderboard";
import CustomGraph from "../../../components/HrManagements/CustomGraph";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

dayjs.extend(utc);
dayjs.extend(timezone);

// ─── Helpers ────────────────────────────────────────────────────────────────

const fmtMins = (m) => {
    if (!m) return "—";
    if (m < 60) return `${Math.round(m)}m`;
    const h = Math.floor(m / 60);
    const mn = Math.round(m % 60);
    return `${h}h${mn > 0 ? ` ${mn}m` : ""}`;
};

const fmtSecs = (s) => {
    if (!s) return "—";
    if (s < 60) return `${Math.round(s)}s`;
    return `${Math.floor(s / 60)}m ${Math.round(s % 60)}s`;
};

const fmtNum = (n) => (n || 0).toLocaleString();
const fmtPct = (v) => `${(v || 0).toFixed(1)}%`;

const initials = (name) =>
    (name || "")
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map((w) => w[0])
        .join("")
        .toUpperCase();

const roleLabel = (r) => ROLE_LABELS[r] || (r === "assistantManager" ? ROLE_LABELS.assistant_manager : r);

const ROLE_COLORS = {
    sr_manager: { bg: "#EEF2FF", color: "#4338CA" },
    team_lead: { bg: "#FFF7ED", color: "#C2410C" },
    agent: { bg: "#F0FDF4", color: "#15803D" },
    manager: { bg: "#FDF4FF", color: "#7E22CE" },
};

const CALL_COLORS = ["#E24B4A", "#378ADD", "#1D9E75", "#BA7517"];
const CALL_LABELS = ["Negative", "Connected", "Positive", "Not Connected"];

// ─── Stat Card ──────────────────────────────────────────────────────────────

const StatCard = ({ label, value, sub, accent, tooltip }) => (
    <Paper
        elevation={0}
        sx={{
            p: 2,
            borderRadius: 2,
            border: "1px solid",
            borderColor: "grey.100",
            bgcolor: accent ? "primary.50" : "grey.50",
            height: "100%",
        }}
    >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
            <Typography
                sx={{ fontSize: 11, fontWeight: 500, color: "text.secondary", textTransform: "uppercase", letterSpacing: "0.05em" }}
            >
                {label}
            </Typography>
            {tooltip && (
                <Tooltip title={tooltip} arrow placement="top">
                    <InfoOutlinedIcon sx={{ fontSize: 13, color: "text.disabled", cursor: "help" }} />
                </Tooltip>
            )}
        </Box>
        <Typography
            sx={{ fontSize: 22, fontWeight: 600, color: accent ? "primary.main" : "text.primary", lineHeight: 1.2 }}
        >
            {value}
        </Typography>
        {sub && (
            <Typography sx={{ fontSize: 11, color: "text.secondary", mt: 0.5 }}>
                {sub}
            </Typography>
        )}
    </Paper>
);
// ─── Status Breakdown ────────────────────────────────────────────────────────

const StatusBreakdown = ({ statusBreakdown }) => {
    if (!statusBreakdown || !Object.keys(statusBreakdown).length) {
        return (
            <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                No status data
            </Typography>
        );
    }
    const entries = Object.entries(statusBreakdown)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 10);
    const max = entries[0]?.[1] || 1;

    return (
        <Stack spacing={0.75}>
            {entries.map(([key, val]) => (
                <Box key={key}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.25 }}>
                        <Typography
                            sx={{ fontSize: 11, color: "text.secondary", textTransform: "capitalize" }}
                        >
                            {key.replace(/_/g, " ")}
                        </Typography>
                        <Typography sx={{ fontSize: 11, fontWeight: 600, color: "text.primary" }}>
                            {fmtNum(val)}
                        </Typography>
                    </Box>
                    <Box sx={{ height: 4, borderRadius: 2, bgcolor: "grey.100", overflow: "hidden" }}>
                        <Box
                            sx={{
                                height: "100%",
                                width: `${Math.round((val / max) * 100)}%`,
                                bgcolor: "primary.main",
                                borderRadius: 2,
                            }}
                        />
                    </Box>
                </Box>
            ))}
        </Stack>
    );
};

// ─── Lead + Call Metrics Section ─────────────────────────────────────────────

const MetricsSection = ({ leadMetrics: lm, callMetrics: cm, chartIdPrefix }) => {
    const callSeries = [
        cm.outcomeBreakdown?.negative || 0,
        cm.outcomeBreakdown?.connected || 0,
        cm.outcomeBreakdown?.positive || 0,
        cm.outcomeBreakdown?.not_connected || 0,
    ];

    return (
        <Box>
            {/* Lead stats */}
            <Typography
                sx={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.07em", color: "text.secondary", mb: 1.5, display: "flex", alignItems: "center", gap: 0.5 }}
            >
                <LeaderboardIcon sx={{ fontSize: 14 }} /> Lead performance
            </Typography>
            <Grid container spacing={1.5} sx={{ mb: 3 }}>
                {[
                    {
                        label: "Total Leads",
                        value: fmtNum(lm.totalLeads),
                        tooltip: "Total number of leads assigned to this user in the selected date range.",
                    },
                    {
                        label: "Prospects",
                        value: fmtNum(lm.prospectLeads),
                        sub: `${fmtPct(lm.prospectRate)} of total`,
                        accent: true,
                        tooltip: "Leads currently in an active or positive status (e.g. hot, follow up, meeting scheduled, deal closed).",
                    },
                    {
                        label: "Disqualified",
                        value: fmtNum(lm.disqualifiedLeads),
                        sub: `${fmtPct(lm.disqualifiedRate)} of total`,
                        accent: true,
                        tooltip: "Leads marked as not interested, unreachable, or otherwise closed out.",
                    },
                    {
                        label: "Not Updated",
                        value: fmtNum(lm.notUpdatedLeads),
                        sub: `${fmtPct(lm.notUpdatedRate)} of total`,
                        accent: true,
                        tooltip: "Leads still in a new, assigned, claimed, or re-assigned state with no status change yet.",
                    },
                ].map((c) => (
                    <Grid item xs={12} sm={3} key={c.label}>
                        <StatCard {...c} />
                    </Grid>
                ))}
            </Grid>

            {/* Status + call chart */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} md={7}>
                    <Paper elevation={0} sx={{ p: 2, border: "1px solid", borderColor: "grey.100", borderRadius: 2, height: "100%" }}>
                        <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 1.5 }}>
                            Lead Status Breakdown
                        </Typography>
                        <StatusBreakdown statusBreakdown={lm.statusBreakdown} />
                    </Paper>
                </Grid>
                <Grid item xs={12} md={5}>
                    <Paper elevation={0} sx={{ p: 2, border: "1px solid", borderColor: "grey.100", borderRadius: 2, height: "100%" }}>
                        <CustomGraph
                            graphTitle="Call Outcomes"
                            series={callSeries}
                            labels={CALL_LABELS}
                            colors={CALL_COLORS}
                            pieHeight={220}
                            pieWidth="100%"
                            legendPosition="bottom"
                        />
                    </Paper>
                </Grid>
            </Grid>

            {/* Call stats */}
            <Typography
                sx={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.07em", color: "text.secondary", mb: 1.5, mt: 2, display: "flex", alignItems: "center", gap: 0.5 }}
            >
                <PhoneIcon sx={{ fontSize: 14 }} /> Call performance
            </Typography>
            <Grid container spacing={1.5}>
                {[
                    {
                        label: "Total Calls",
                        value: fmtNum(cm.totalCalls),
                        sub: `${fmtNum(cm.completedCalls)} completed`,
                        tooltip: "Total number of calls logged in the selected date range.",
                    },
                    {
                        label: "Connection Rate",
                        value: fmtPct(cm.connectionRate),
                        accent: true,
                        tooltip: "Percentage of total calls that were connected, positive, or negative (i.e. not marked as not-connected).",
                    },
                    {
                        label: "Positive Rate",
                        value: fmtPct(cm.positiveRate),
                        accent: true,
                        tooltip: "Percentage of total calls with a positive outcome.",
                    },
                    {
                        label: "Avg Duration",
                        value: fmtSecs(cm.avgDurationSeconds),
                        tooltip: "Average duration of completed calls.",
                    },
                    {
                        label: "Outbound",
                        value: fmtNum(cm.directionBreakdown?.outbound || 0),
                        tooltip: "Number of calls initiated by this user or team.",
                    },
                    {
                        label: "Inbound",
                        value: fmtNum(cm.directionBreakdown?.inbound || 0),
                        tooltip: "Number of calls received from a lead or client.",
                    },
                ].map((c) => (
                    <Grid item xs={6} sm={4} md={2} key={c.label}>
                        <StatCard {...c} />
                    </Grid>
                ))}
            </Grid>
        </Box>
    );
};

// ─── Member Row (expandable) ──────────────────────────────────────────────────

const MemberRow = ({ member, index }) => {
    const [open, setOpen] = useState(false);
    const lm = member.leadMetrics;
    const cm = member.callMetrics;
    const roleStyle = ROLE_COLORS[member.role] || { bg: "grey.100", color: "text.secondary" };

    return (
        <Paper
            elevation={0}
            sx={{
                border: "1px solid",
                borderColor: open ? "primary.200" : "grey.100",
                borderRadius: 2,
                overflow: "hidden",
                transition: "border-color 0.15s",
            }}
        >
            {/* Header row */}
            <Box
                onClick={() => setOpen(!open)}
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    p: 1.5,
                    cursor: "pointer",
                    "&:hover": { bgcolor: "grey.50" },
                }}
            >
                <Avatar sx={{ width: 34, height: 34, bgcolor: roleStyle.bg, color: roleStyle.color, fontSize: 13, fontWeight: 600 }}>
                    {initials(member.name)}
                </Avatar>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: "text.primary" }}>
                            {member.name.trim()}
                        </Typography>
                        <Chip
                            label={roleLabel(member.role)}
                            size="small"
                            sx={{ height: 18, fontSize: 10, bgcolor: roleStyle.bg, color: roleStyle.color, fontWeight: 500 }}
                        />
                        <Typography sx={{ fontSize: 11, color: "text.disabled" }}>{member.customId}</Typography>
                    </Box>
                </Box>

                {/* Quick stats */}
                <Box sx={{ display: "flex", gap: 3, flexShrink: 0, display: { xs: "none", sm: "flex" } }}>
                    {[
                        { label: "Leads", value: fmtNum(lm.totalLeads) },
                        { label: "Prosp%", value: fmtPct(lm.prospectRate) },
                        { label: "Disqualify%", value: fmtPct(lm.disqualifiedRate) },
                        { label: "Calls", value: fmtNum(cm.totalCalls) },
                        { label: "Connect%", value: fmtPct(cm.connectionRate) },
                        { label: "Positive%", value: fmtPct(cm.positiveRate) },
                    ].map((s) => (
                        <Box key={s.label} sx={{ textAlign: "center" }}>
                            <Typography sx={{ fontSize: 13, fontWeight: 600, color: "text.primary" }}>{s.value}</Typography>
                            <Typography sx={{ fontSize: 10, color: "text.secondary" }}>{s.label}</Typography>
                        </Box>
                    ))}
                </Box>

                <IconButton size="small" sx={{ transform: open ? "rotate(180deg)" : "none", transition: "transform 0.2s" }}>
                    <ExpandMoreIcon fontSize="small" />
                </IconButton>
            </Box>

            {/* Expanded detail */}
            <Collapse in={open}>
                <Divider />
                <Box sx={{ p: 2 }}>
                    <MetricsSection
                        leadMetrics={lm}
                        callMetrics={cm}
                        chartIdPrefix={`member_${index}`}
                    />
                </Box>
            </Collapse>
        </Paper>
    );
};

// ─── Team Panel (single team) ─────────────────────────────────────────────────

const TeamPanel = ({ team }) => {
    const r = team.teamRollup;
    return (
        <Box>
            {/* Team rollup summary */}
            <Grid container spacing={1.5} sx={{ mb: 3 }}>
                {[
                    {
                        label: "Team Leads",
                        value: fmtNum(r.leadMetrics.totalLeads),
                        tooltip: "Total number of leads assigned across this team in the selected date range.",
                    },
                    {
                        label: "Prospects",
                        value: fmtNum(r.leadMetrics.prospectLeads),
                        sub: `${fmtPct(r.leadMetrics.prospectRate)} of total`,
                        accent: true,
                        tooltip: "Leads currently in an active or positive status (e.g. hot, meeting scheduled, deal closed).",
                    },
                    {
                        label: "Disqualified",
                        value: fmtNum(r.leadMetrics.disqualifiedLeads),
                        sub: `${fmtPct(r.leadMetrics.disqualifiedRate)} of total`,
                        accent: true,
                        tooltip: "Leads marked as not interested, unreachable, or otherwise closed out.",
                    },
                    // {
                    //     label: "Avg Update Time",
                    //     value: fmtMins(r.leadMetrics.avgTimeToFirstUpdateMinutes),
                    //     tooltip: "Average time between a lead being assigned and its first status change or note.",
                    // },
                    {
                        label: "Total Calls",
                        value: fmtNum(r.callMetrics.totalCalls),
                        tooltip: "Total number of calls logged by this team in the selected date range.",
                    },
                    {
                        label: "Connect Rate",
                        value: fmtPct(r.callMetrics.connectionRate),
                        accent: true,
                        tooltip: "Percentage of total calls that were connected, positive, or negative (i.e. not marked as not-connected).",
                    },
                    {
                        label: "Positive Rate",
                        value: fmtPct(r.callMetrics.positiveRate),
                        tooltip: "Percentage of total calls that were positive (i.e marked as positive).",
                    },
                ].map((c) => (
                    <Grid item xs={6} sm={4} md={2} key={c.label}>
                        <StatCard {...c} />
                    </Grid>
                ))}
            </Grid>

            <Typography sx={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.07em", color: "text.secondary", mb: 1.5 }}>
                Members
            </Typography>
            <Stack spacing={1}>
                {team.members.map((m, i) => (
                    <MemberRow key={m.userId} member={m} index={`${team.teamId}_${i}`} />
                ))}
            </Stack>
        </Box>
    );
};

// ─── Main Report Component ────────────────────────────────────────────────────

const UserPerformanceReport = ({ reportData }) => {
    const [teamTab, setTeamTab] = useState(0);

    if (!reportData) {
        return (
            <Typography sx={{ color: "text.secondary", p: 3 }}>No report data available.</Typography>
        );
    }

    const { reportType, user, dateRange, personal, teamBreakdown, overallTeamRollup } = reportData;
    const isAgent = reportType === "agent";
    const roleStyle = ROLE_COLORS[user.role] || { bg: "grey.100", color: "text.secondary" };

    // const dateText =
    //     dateRange === "all-time"
    //         ? "All time"
    //         : `${dateRange.from ? dayjs.utc(dateRange.from).local().format("YYYY-MM-DD") : "—"} → ${dateRange.to ? dayjs.utc(dateRange.to).local().format("YYYY-MM-DD") : "—"
    //         }`;

    return (
        <Box sx={{ p: { xs: 2, md: 3 }, mx: "auto", bgcolor: "#ffffffe7", borderColor: "grey.500", borderRadius: 2, }}>
            {/* ── Report header ── */}
            <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: 3, gap: 2, flexWrap: "wrap" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Avatar
                        sx={{ width: 52, height: 52, bgcolor: roleStyle.bg, color: roleStyle.color, fontSize: 17, fontWeight: 700 }}
                    >
                        {initials(user.name)}
                    </Avatar>
                    <Box>
                        <Typography sx={{ fontSize: 20, fontWeight: 700, color: "text.primary" }}>
                            {user.name.trim()}
                        </Typography>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                            <Chip
                                label={roleLabel(user.role)}
                                size="small"
                                sx={{ bgcolor: roleStyle.bg, color: roleStyle.color, fontWeight: 600, fontSize: 11 }}
                            />
                            <Typography sx={{ fontSize: 12, color: "text.disabled" }}>{user.customId}</Typography>
                        </Box>
                    </Box>
                </Box>
                {/* <Chip
                    label={dateText}
                    variant="outlined"
                    size="small"
                    sx={{ fontSize: 12, borderColor: "grey.300", color: "text.secondary" }}
                /> */}
            </Box>

            {/* ── Personal performance ── */}
            <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, border: "1px solid", borderColor: "grey.100", borderRadius: 2, mb: 3 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2.5 }}>
                    <PersonIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                    <Typography sx={{ fontSize: 13, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: "text.secondary" }}>
                        {isAgent ? "Performance overview" : "Personal performance"}
                    </Typography>
                </Box>
                <MetricsSection
                    leadMetrics={personal.leadMetrics}
                    callMetrics={personal.callMetrics}
                    chartIdPrefix="personal"
                />
            </Paper>

            {/* ── Team sections (non-agent) ── */}
            {!isAgent && teamBreakdown && teamBreakdown.length > 0 && (
                <>
                    {/* Overall team rollup */}
                    <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, border: "1px solid", borderColor: "grey.100", borderRadius: 2, mb: 3 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2.5 }}>
                            <GroupsIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                            <Typography sx={{ fontSize: 13, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: "text.secondary" }}>
                                Overall team rollup
                            </Typography>
                        </Box>

                        {overallTeamRollup && (
                            <Grid container spacing={1.5}>
                                {[
                                    {
                                        label: "Total Leads",
                                        value: fmtNum(overallTeamRollup.leadMetrics.totalLeads),
                                        tooltip: "Total number of leads assigned across all teams in the selected date range.",
                                    },
                                    {
                                        label: "prospects",
                                        value: fmtNum(overallTeamRollup.leadMetrics.prospectLeads),
                                        sub: `${fmtPct(overallTeamRollup.leadMetrics.prospectRate)} rate`,
                                        accent: true,
                                        tooltip: "Leads currently in an active or positive status (e.g. hot, meeting scheduled, deal closed).",
                                    },
                                    {
                                        label: "Disqualified",
                                        value: fmtNum(overallTeamRollup.leadMetrics.disqualifiedLeads),
                                        sub: `${fmtPct(overallTeamRollup.leadMetrics.disqualifiedRate)} rate`,
                                        accent: true,
                                        tooltip: "Leads marked as not interested, unreachable, or otherwise closed out.",
                                    },
                                    {
                                        label: "Leads Not Updated",
                                        value: fmtNum(overallTeamRollup.leadMetrics.notUpdatedLeads),
                                        tooltip: "Leads still in a new, assigned, claimed, or re-assigned state with no status change yet.",
                                    },
                                    {
                                        label: "Total Calls",
                                        value: fmtNum(overallTeamRollup.callMetrics.totalCalls),
                                        tooltip: "Total number of calls logged across all teams in the selected date range.",
                                    },
                                    {
                                        label: "Connect Rate",
                                        value: fmtPct(overallTeamRollup.callMetrics.connectionRate),
                                        accent: true,
                                        tooltip: "Percentage of total calls that were connected, positive, or negative (i.e. not marked as not-connected).",
                                    },
                                    {
                                        label: "Positive Rate",
                                        value: fmtPct(overallTeamRollup.callMetrics.positiveRate),
                                        tooltip: "Percentage of total calls with a positive outcome.",
                                    },
                                    {
                                        label: "Avg Duration",
                                        value: fmtSecs(overallTeamRollup.callMetrics.avgDurationSeconds),
                                        tooltip: "Average duration of completed calls.",
                                    },
                                ].map((c) => (
                                    <Grid item xs={6} sm={3} md={3} key={c.label}>
                                        <StatCard {...c} />
                                    </Grid>
                                ))}
                            </Grid>
                        )}
                    </Paper>

                    {/* Team breakdown by tab */}
                    <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, border: "1px solid", borderColor: "grey.100", borderRadius: 2 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                            <GroupsIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                            <Typography sx={{ fontSize: 13, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: "text.secondary" }}>
                                Team breakdown
                            </Typography>
                        </Box>

                        <Tabs
                            value={teamTab}
                            onChange={(_, v) => setTeamTab(v)}
                            variant="scrollable"
                            scrollButtons="auto"
                            sx={{
                                mb: 2.5,
                                minHeight: 36,
                                "& .MuiTab-root": { minHeight: 36, fontSize: 12, fontWeight: 500, textTransform: "none", py: 0.5 },
                                "& .MuiTabs-indicator": { height: 2 },
                            }}
                        >
                            {teamBreakdown.map((team) => (
                                <Tab
                                    key={team.teamId}
                                    label={
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                                            {team.teamName}
                                            <Chip
                                                label={team.members.length}
                                                size="small"
                                                sx={{ height: 16, fontSize: 10, "& .MuiChip-label": { px: 0.75 } }}
                                            />
                                        </Box>
                                    }
                                />
                            ))}
                        </Tabs>

                        {teamBreakdown.map((team, i) => (
                            <Box key={team.teamId} role="tabpanel" hidden={teamTab !== i}>
                                {teamTab === i && <TeamPanel team={team} />}
                            </Box>
                        ))}
                    </Paper>
                </>
            )}
        </Box>
    );
};

export default UserPerformanceReport;